/**
 * Configurações › Sistema › Pesquisa de satisfação (D30, mig 796): as regras do motor. Só o admin (D56). Nasce
 * DESLIGADA; ligar PERGUNTA antes, com a consequência escrita. A mensagem do WhatsApp NÃO mora aqui: é o aviso
 * "OS: pesquisa de satisfação" de Integrações › WhatsApp (D35), com o seu próprio liga/desliga.
 */
import { useEffect, useState } from 'react';

import { Button, Input, Label } from '@/components/ui';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/components/ui/Toast';
import { api, ApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';

interface Config {
  ligada: boolean;
  minutosDepois: number;
  horasValidade: number;
  canalPortal: boolean;
  perguntaNota: string;
  perguntaComentario: string;
  mudadoPor: string | null;
  mudadoEmIso: string | null;
}

const B = '/api/v1/os/pesquisa/config';

export function ConfiguracaoDaPesquisa() {
  const [c, setC] = useState<Config | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const confirmar = useConfirmDialog();
  const { show } = useToast();

  useEffect(() => {
    api
      .get<Config>(B)
      .then(setC)
      .catch(() => setErro('Não deu para abrir a configuração da pesquisa.'));
  }, []);

  if (!c) return erro ? <p role="alert">{erro}</p> : <p className="text-sm text-ink-3">Abrindo…</p>;

  async function salvar(proxima: Config) {
    if (proxima.ligada && !c!.ligada) {
      const ok = await confirmar({
        title: 'Ligar a pesquisa de satisfação?',
        message: `Depois de cada atendimento concluído (${proxima.minutosDepois} min depois do check-out), nasce uma pesquisa para o cliente, uma por dia. Quem entra no Portal passa a vê-la. O aviso do WhatsApp só sai se também estiver ligado em Integrações › WhatsApp.`,
        confirmLabel: 'Ligar',
      });
      if (!ok) return;
    }
    setErro(null);
    setSalvando(true);
    try {
      const { mudadoPor: _q, mudadoEmIso: _e, ...corpo } = proxima;
      setC(await api.put<Config>(B, corpo));
      show('Pesquisa salva.', { variant: 'success' });
    } catch (e) {
      setErro(e instanceof ApiError ? ((e.response as { mensagem?: string } | undefined)?.mensagem ?? e.message) : 'Não deu para salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4" data-testid="configuracao-da-pesquisa">
      <label className="flex min-h-11 items-center gap-2 text-sm font-semibold">
        <input type="checkbox" className="h-5 w-5" checked={c.ligada} onChange={(e) => setC({ ...c, ligada: e.target.checked })} />
        Pesquisa ligada
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="pes-minutos">Minutos depois do check-out</Label>
          <Input id="pes-minutos" type="number" min={0} max={1440} value={c.minutosDepois} onChange={(e) => setC({ ...c, minutosDepois: Number(e.target.value) })} />
        </div>
        <div>
          <Label htmlFor="pes-horas">O link vale por (horas)</Label>
          <Input id="pes-horas" type="number" min={1} max={720} value={c.horasValidade} onChange={(e) => setC({ ...c, horasValidade: Number(e.target.value) })} />
        </div>
      </div>
      <p className="text-sm text-ink-2 dark:text-ink-dark-2">Uma pesquisa por cliente por dia. Cliente da Route não recebe.</p>
      <div>
        <Label htmlFor="pes-nota">Pergunta da nota (de 0 a 10)</Label>
        <Input id="pes-nota" value={c.perguntaNota} onChange={(e) => setC({ ...c, perguntaNota: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="pes-comentario">Pergunta do comentário (opcional para o cliente)</Label>
        <Input id="pes-comentario" value={c.perguntaComentario} onChange={(e) => setC({ ...c, perguntaComentario: e.target.value })} />
      </div>
      <label className="flex min-h-11 items-center gap-2 text-sm">
        <input type="checkbox" className="h-5 w-5" checked={c.canalPortal} onChange={(e) => setC({ ...c, canalPortal: e.target.checked })} />
        Mostrar no Portal do Cliente
      </label>
      <p className="text-sm text-ink-2 dark:text-ink-dark-2">
        O link também pode ir pelo WhatsApp: o texto, o liga/desliga e os grupos ficam em Integrações › WhatsApp, no aviso
        &quot;OS: pesquisa de satisfação&quot;.
      </p>
      {erro ? (
        <p role="alert" className="text-sm font-semibold text-red-deep dark:text-red-glow">
          {erro}
        </p>
      ) : null}
      <div className="flex items-center gap-3">
        <Button onClick={() => void salvar(c)} disabled={salvando} className="min-h-[44px] md:min-h-0">
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
        {c.mudadoEmIso ? (
          <span className="text-xs text-ink-3 dark:text-ink-dark-3">
            Mudado por {c.mudadoPor ?? 'alguém'} em {formatDateTime(c.mudadoEmIso)}.
          </span>
        ) : null}
      </div>
    </div>
  );
}
